import { useContext, useEffect, useRef } from 'react';
import { GAME_HAND_PHASE, TRAY_OPEN_CARD_LIFT_MS } from 'constants/gameConstants';
import { DealerAnimationContext } from 'contexts/DealerAnimationContext';
import DealerChipContext from 'contexts/DealerChipContext';
import GameContext from 'contexts/GameContext';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import PovCardsContext from 'contexts/PovCardsContext';
import { useShowAllDealers } from 'contexts/ShowAllDealersContext';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { useRegisterFloatingAnchor } from 'hooks/useFloatingRegistry';
import { useTableSelector } from 'hooks/useTableSelector';
import { useTrayClearanceLift } from 'hooks/useTrayClearanceLift';
import { playerPotWin, shouldDimCard } from 'utils/functions';

import HandRank from 'components/HandRank/HandRank';
import { DealerChip, PovCards, SeatCardPill } from 'components/Seat/components';
import { getSeatDealerOffset } from 'components/Seat/styles';

import { getLandscapeModeStyles, useSeatAnimation } from './index';
import { isLivePovCardObservation } from './povCardVisibility';

type LandscapeCurrentPlayerProps = {
  seat: any;
  seatId: number;
  currentPlayerSeatId: number;
  isFaceCardUp: boolean;
  position: SeatPosition;
  cards: any;
  hasAction: boolean;
  playerAction: string;
  displayAction?: string;
  actionColor: any;
  isWinner: boolean;
  isTurn: boolean;
  cardPosition: any;
  isRotating?: boolean;
};

export const LandscapeCurrentPlayer = ({
  seat,
  seatId,
  currentPlayerSeatId,
  isFaceCardUp,
  position,
  cards,
  hasAction,
  playerAction,
  displayAction,
  actionColor,
  isWinner,
  isTurn,
  cardPosition,
  isRotating = false
}: LandscapeCurrentPlayerProps) => {
  const game = useTableSelector((table) => table.game);
  const gameState = useTableSelector((table) => table.gameState);
  const isPlayerFolded = useTableSelector((table) => table.currentPlayer.isFolded);
  const isPlayerJoined = useTableSelector((table) => table.currentPlayer.isJoined);
  const isPlayerWaiting = useTableSelector((table) => table.currentPlayer.isWaiting);
  const isPlayerWaitingAndExit = useTableSelector((table) => table.currentPlayer.isWaitingAndExit);
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  const { isAnimating, dealer } = useContext(DealerAnimationContext);
  const { isBetTrayOpen } = useContext(GameContext);
  // EN-10420: the wide/desktop tray covers the POV seat on shorter landscape
  // canvases (laptops, half-snapped windows). Unlike portrait — where only the
  // decision group rises and the stack row stays deliberately covered — the
  // WHOLE landscape seat travels: cards, hand strength, avatar, timer and
  // balance keep their exact spatial relationship and simply sit higher
  // (Jason 8/13). One transform on a wrapper ABOVE seatRef, because
  // useSeatAnimation drives gsap transforms on seatRef itself and the two
  // would fight. Resolves to `none` wherever nothing is occluded.
  const povGroupRef = useRef<HTMLDivElement>(null);
  const trayLiftTransform = useTrayClearanceLift(isBetTrayOpen, povGroupRef);
  // Publish the travelling block so this seat's bet chip (Table/Bet) can rise
  // by the SAME amount and keep its spacing instead of being crowded.
  useRegisterFloatingAnchor(PovCardsContext, seatId, povGroupRef);
  const seatRef = useRef<HTMLDivElement>(null);
  const turnGlowRef = useRef<HTMLDivElement>(null);
  const opponentGlowRef = useRef<HTMLDivElement>(null);
  const dealerChipRef = useRef<HTMLDivElement>(null);
  const handNum = Number(game.hand);
  const hasActiveHandPhase = Number.isFinite(handNum);
  // Presentation gate only: these statuses should not show local cards until the
  // server moves the player into an active hand, matching Unity's UIPlayer flow.
  const isWaitingForHand = isPlayerJoined || isPlayerWaiting || isPlayerWaitingAndExit;
  const hasSeenPreDealRef = useRef(
    !hasActiveHandPhase || handNum < GAME_HAND_PHASE.PRE_FLOP_BETTING_ROUND
  );
  // Tracks whether this UI actually displayed POV cards in the hand; prevents
  // end-of-hand collect animation from rendering stale setup/join cards.
  const hasSeenPovCardsRef = useRef(false);

  const showAllDealers = useShowAllDealers();
  const showDealerChip =
    showAllDealers || (!!game.dealerVisible && seatId === dealer && !isAnimating);

  useRegisterFloatingAnchor(DealerChipContext, seatId, dealerChipRef);

  useSeatAnimation(isWinner, isTurn, false, seatRef, turnGlowRef, opponentGlowRef);
  const { getMyPlayerWrapperStyle, getMyPlayerContentStyle } = getLandscapeModeStyles(
    position,
    isRotating
  );

  const showCards = game.seatsWithVisibleCards?.includes(seatId);
  useEffect(() => {
    if (
      gameState.preDeal ||
      !hasActiveHandPhase ||
      handNum < GAME_HAND_PHASE.PRE_FLOP_BETTING_ROUND
    ) {
      hasSeenPreDealRef.current = true;
    }
  }, [gameState.preDeal, handNum, hasActiveHandPhase]);

  const hasCards = cards?.some((card: string) => !!card);
  useEffect(() => {
    if (isLivePovCardObservation({ showCards, hasActiveHandPhase, handNum, isWaitingForHand })) {
      hasSeenPovCardsRef.current = true;
    }

    if (!hasCards || isWaitingForHand) {
      hasSeenPovCardsRef.current = false;
    }
  }, [handNum, hasActiveHandPhase, hasCards, isWaitingForHand, showCards]);

  const isShowCardDecision =
    !!gameState.pokerActions.showCard ||
    !!gameState.pokerActions.muckCard ||
    gameState.startMucking ||
    game.seatsWithShowCard?.includes(seatId) ||
    game.seatsWithMuckCard?.includes(seatId);
  const hasShownCards = game.seatsWithShowCard?.includes(seatId);
  const shouldForcePovFaceDown = !showCards && isShowCardDecision && !hasShownCards;
  // Unity returns cards on RecordStats/AddOnsAndCharging. This is animation
  // visibility state, not poker rule evaluation.
  const shouldCollectPovCards =
    !isHandHistorySnapshot &&
    hasCards &&
    hasActiveHandPhase &&
    handNum >= GAME_HAND_PHASE.RECORD_NEW_STATS_AND_START_NEW_HAND &&
    hasSeenPovCardsRef.current &&
    !isWaitingForHand;
  // The deal window is hand 4 (DEAL_CARDS), when every seat is dealt at once:
  // this seat gets face-down placeholders there and turns them up when the
  // real cards arrive at hand 5 (EN-10985). Hand 5 (PRE_FLOP_BETTING_ROUND,
  // Unity's "DealCards") stays in the window for a client that never saw 4.
  const shouldAnimatePovDeal =
    !isHandHistorySnapshot &&
    hasSeenPreDealRef.current &&
    showCards &&
    hasActiveHandPhase &&
    (handNum === GAME_HAND_PHASE.DEAL_CARDS ||
      handNum === GAME_HAND_PHASE.PRE_FLOP_BETTING_ROUND) &&
    !isWaitingForHand;
  const showPovCards = isHandHistorySnapshot
    ? showCards || hasCards
    : !isWaitingForHand &&
      (showCards || shouldCollectPovCards || ((isPlayerFolded || isShowCardDecision) && hasCards));
  const nameSize = '0.65rem';
  const stackSize = '0.65rem';

  return (
    <div
      style={
        // The POV seat statically outranks its own bet chip and the dealer
        // button (wrapper z6 vs their z5 — the 8/13 z-order model in
        // getLandscapeModeStyles). This subsumes the earlier tray-open-only
        // bump: the chip stays exactly where a player expects to read their
        // current bet, just behind the ring/cards it used to obscure.
        getMyPlayerWrapperStyle()
      }>
      {/* Tray-clearance layer: the whole seat rides up together (see the hook
          note above). Separate from seatRef so gsap keeps sole ownership of
          that element's transform. */}
      <div
        ref={povGroupRef}
        data-pov-decision-group
        style={{
          transform: trayLiftTransform,
          transition: `transform ${TRAY_OPEN_CARD_LIFT_MS}ms ease-out`
        }}>
        <div ref={seatRef} style={getMyPlayerContentStyle()}>
          {/* POV DECISION GROUP — cards to the left of the pill, with the
          hand-strength label directly UNDER THE CARDS it describes (Jason
          8/12: it previously hung under the avatar/stack pill, describing the
          wrong thing). One container, one transform, so the pair travels
          together when the bet tray forces them clear. */}
          {showPovCards && (
            <div
              style={{
                position: 'absolute',
                right: '110%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px'
              }}>
              <PovCards
                cards={cards}
                elevation={4}
                isFaceDown={shouldForcePovFaceDown}
                isDimmed={(card) => isPlayerFolded || shouldDimCard(game, card)}
                shouldAnimateDeal={shouldAnimatePovDeal}
                shouldCollect={shouldCollectPovCards}
                style={{ flexShrink: 0 }}
              />
              {isFaceCardUp && <HandRank />}
            </div>
          )}

          {/* Avatar + info pill */}
          <SeatCardPill
            seat={seat}
            seatId={seatId}
            currentPlayerSeatId={currentPlayerSeatId}
            isFaceCardUp={isFaceCardUp}
            isPortrait={false}
            hasAction={hasAction}
            playerAction={playerAction}
            displayAction={displayAction}
            actionColor={actionColor}
            isWinner={isWinner}
            nameSize={nameSize}
            stackSize={stackSize}
            cardPosition={cardPosition}
            glowRef={turnGlowRef}
          />

          {/* Hand strength now lives with the cards (see the decision group above).
          It still needs a home when the cards are not rendered — folded, mucked,
          or pre-deal — where it hugs the pill as it always did. The winnings
          badge (AmountWon, from SeatCardPill) also sits at top:100% at showdown,
          so on a win it drops beneath that to avoid the overlap. */}
          {isFaceCardUp && !showPovCards && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginTop: playerPotWin(game.pots, game.showdown, seatId) > 0 ? '34px' : '4px',
                transition: 'margin-top 0.2s ease'
              }}>
              <HandRank />
            </div>
          )}
        </div>
      </div>

      {/* Dealer chip - visible only when this seat has dealer and button is not
        animating. Deliberately OUTSIDE the tray-clearance layer above: the
        button marks the dealer's position at the table, so opening the bet tray
        must not move it (Jason 8/13). It stays anchored to the seat wrapper,
        which does not travel — same place, tray open or closed. */}
      <div
        ref={dealerChipRef}
        style={{
          // Inside the seat now (was `right: -50px`, i.e. floating off it) —
          // the POV's hole cards live at `right: 110%`, so the seat's own
          // bottom-right corner is the free spot nearest the table centre.
          ...getSeatDealerOffset(position, false),
          opacity: showDealerChip ? 1 : 0,
          pointerEvents: showDealerChip ? 'auto' : 'none'
        }}>
        <DealerChip isPortrait={false} />
      </div>
    </div>
  );
};
